import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserProfile } from "@/types/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDashboardData } from "@/components/dashboard/useDashboardData";

type CreateGroupChatDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateGroup: (name: string, members: UserProfile[]) => void;
  currentUserId: string;
};

export function CreateGroupChatDialog({
  open,
  onOpenChange,
  onCreateGroup,
  currentUserId,
}: CreateGroupChatDialogProps) {
  const [groupName, setGroupName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<UserProfile[]>([]);
  const { teamMembers } = useDashboardData();

  // Filter out the current user from team members
  const availableMembers = teamMembers.filter(
    (member) => member.id !== currentUserId
  );

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setGroupName("");
      setSelectedMembers([]);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (groupName.trim() === "" || selectedMembers.length === 0) return;
    onCreateGroup(groupName, selectedMembers);
    onOpenChange(false);
  };

  const handleToggleMember = (member: UserProfile) => {
    setSelectedMembers((current) => {
      const exists = current.some((m) => m.id === member.id);
      if (exists) {
        return current.filter((m) => m.id !== member.id);
      } else {
        return [...current, member];
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Group Chat</DialogTitle>
            <DialogDescription>
              Create a new group conversation with team members.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="group-name" className="text-right">
                Group Name
              </Label>
              <Input
                id="group-name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="col-span-3"
                placeholder="e.g., Project Team"
                required
              />
            </div>

            <div className="grid grid-cols-4 items-start gap-4 mt-2">
              <Label className="text-right pt-2">Members</Label>
              <ScrollArea className="h-[200px] col-span-3 border rounded-md p-2">
                <div className="space-y-2">
                  {availableMembers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2 text-center">
                      No team members available
                    </p>
                  ) : (
                    availableMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={`member-${member.id}`}
                          checked={selectedMembers.some(
                            (m) => m.id === member.id
                          )}
                          onCheckedChange={() => handleToggleMember(member)}
                        />
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.avatarUrl} />
                            <AvatarFallback>
                              {member.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <Label
                            htmlFor={`member-${member.id}`}
                            className="cursor-pointer flex-1"
                          >
                            {member.name}
                          </Label>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={groupName.trim() === "" || selectedMembers.length === 0}
            >
              Create Group
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
